import { SportsApiClient } from '@collector/sportsapi-client'

import {
  AddIncident,
  ChangeOrderIncident,
  DeleteIncident,
  UpdateIncident,
  type AddIncidentPayload,
  type UpdateIncidentPayload,
} from './incidents/index.js'
import {
  IncidentQueueTimeoutError,
  type BeforeQueueIncidentProcessing,
  type ConfirmQueueIncident,
  type OnChange,
  type OnChangePayload,
  type ProcessAllowedQueueIncidentError,
  type QueueIncident,
  type QueueStatus,
} from './types.js'
import {
  confirmProcessedQueueIncident,
  dequeue,
  enqueue,
  getQueueIncidentUuid,
  isAddIncident,
  peek,
} from './utils/index.js'

export class EventIncidentsQueue {
  eventId: number
  sportsApiClient: SportsApiClient
  queue: QueueIncident[] = []
  queueStatus: QueueStatus = 'idle'

  onChangeCallbacks: OnChange[] = []

  confirmQueueIncident!: ConfirmQueueIncident

  /**
   * Error which occured during processing last incident; if null, last incident was processed successfully
   */
  currentError: unknown = null

  /**
   * Optional function to check if an error should be treated as "allowed" (not an actual error)
   */
  private processAllowedQueueIncidentError?: ProcessAllowedQueueIncidentError

  /**
   * Optional function to execute before processing each queue incident
   */
  private beforeQueueIncidentProcessing?: BeforeQueueIncidentProcessing

  constructor(eventId: number, sportsApiClient: SportsApiClient) {
    this.eventId = eventId
    this.sportsApiClient = sportsApiClient
  }

  /**
   * Sets error on queue incident and updates current error state
   */
  private setQueueIncidentError(
    queueIncident: QueueIncident,
    error: unknown,
  ): void {
    queueIncident.setStatus('Error')
    queueIncident.error = error

    this.currentError = error
  }

  public setProcessAllowedQueueIncidentError(
    processAllowedQueueIncidentError: ProcessAllowedQueueIncidentError,
  ): void {
    this.processAllowedQueueIncidentError = processAllowedQueueIncidentError
  }

  /**
   * Set function to execute before processing each queue incident
   */
  public setBeforeQueueIncidentProcessing(
    beforeQueueIncidentProcessing: BeforeQueueIncidentProcessing,
  ): void {
    this.beforeQueueIncidentProcessing = beforeQueueIncidentProcessing
  }

  /**
   * Set confirmation function which acknowledges that incident was successfully processed by API,
   * or executes callback with error if not processed successfully.
   */
  public setConfirmQueueIncident(
    confirmQueueIncident: ConfirmQueueIncident,
  ): void {
    this.confirmQueueIncident = confirmQueueIncident
  }

  /**
   * Enqueue incident payload to create Incident in API
   */
  public addIncident(payload: AddIncidentPayload, parentUuid?: string): string {
    const newIncident = new AddIncident(
      this.eventId,
      payload,
      undefined,
      parentUuid,
    )
    this.enqueueIncident(newIncident)

    return newIncident.uuid
  }

  /**
   * Enqueue incident which updates existing Incident in API
   */
  public updateIncident(
    payload: UpdateIncidentPayload,
    id: UpdateIncident['id'],
  ): void {
    this.enqueueIncident(new UpdateIncident(this.eventId, payload, id))
  }

  public changeIncidentOrder(
    incidentId: ChangeOrderIncident['id'],
    action: ChangeOrderIncident['action'],
  ): void {
    this.enqueueIncident(
      new ChangeOrderIncident(this.eventId, incidentId, action),
    )
  }

  /**
   * Enqueue incident which marks existing Incident as deleted in API
   */
  public deleteIncident(id: DeleteIncident['id']): void {
    this.enqueueIncident(new DeleteIncident(this.eventId, id))
  }

  /**
   * Remove incident from queue to prevent processing
   */
  public removeQueueIncident(index: number): void {
    const queueIncident = dequeue(this.queue, index)
    const uuid = getQueueIncidentUuid(queueIncident)

    this.onChange(uuid)
  }

  /**
   * Add callback to be called on queue change
   */
  public subscribeOnChange(callback: OnChange): void {
    this.onChangeCallbacks.push(callback)

    this.runOnChange(callback, this.getOnChangePayload())
  }

  /**
   * Removed callback passed to `subscribeOnChange`
   */
  public unsubscribeOnChange(callback: OnChange): void {
    const index = this.onChangeCallbacks.indexOf(callback)

    if (index !== -1) {
      this.onChangeCallbacks.splice(index, 1)
    }
  }

  /**
   * Process all incidents in queue from beginning, including incidents with error
   */
  public rerunQueue(): Promise<void> {
    if (this.queueStatus === 'idle') {
      return this.startProcessing(true)
    } else {
      throw Error('Cannot rerun when queue is not idle')
    }
  }

  dequeueIfExists(queueIncident: QueueIncident): void {
    const queueIncidentIndex = this.queue.indexOf(queueIncident)

    if (queueIncidentIndex !== -1) {
      dequeue(this.queue, queueIncidentIndex)
    }
  }

  private async processQueueIncident(
    queueIncident: QueueIncident,
  ): Promise<void> {
    queueIncident.setStatus('In Progress')
    queueIncident.error = null
    this.onChange()

    try {
      // Execute beforeQueueIncidentProcessing hook if provided
      if (this.beforeQueueIncidentProcessing) {
        await this.beforeQueueIncidentProcessing(queueIncident)
      }

      const uuid = await queueIncident.process(this.sportsApiClient)

      if (queueIncident.isConfirmable) {
        await confirmProcessedQueueIncident(uuid, this.confirmQueueIncident)
      }

      this.dequeueIfExists(queueIncident)
    } catch (error) {
      try {
        if (
          error instanceof IncidentQueueTimeoutError &&
          queueIncident.canRetry() &&
          isAddIncident(queueIncident)
        ) {
          queueIncident.incrementAutoRetryCount()
          queueIncident.setStatus('Pending') // Reset status for retry
          this.onChange()

          // Retry processing the same `queueIncident` after timeout
          //
          // NOTE: This is a recursive call
          // - the infinite loop risk by calling `processQueueIncident` again
          // is mitigated by the retry count limit checked in `canRetry()`
          await this.processQueueIncident(queueIncident)
          return
        }

        // Check if this error should be treated as "allowed" (not an actual error)
        const isAllowedQueueIncidentError = this
          .processAllowedQueueIncidentError
          ? await this.processAllowedQueueIncidentError(error, queueIncident)
          : false

        if (isAllowedQueueIncidentError) {
          // Treat allowed errors as success - remove the incident from queue
          this.dequeueIfExists(queueIncident)
        } else {
          this.setQueueIncidentError(queueIncident, error)
        }
      } catch (err) {
        this.setQueueIncidentError(queueIncident, err)
      }
    }
  }

  private runOnChange(callback: OnChange, payload: OnChangePayload): void {
    callback(payload)
  }

  getOnChangePayload(): OnChangePayload {
    const currentError = this.currentError

    if (currentError) {
      this.currentError = null
    }

    return {
      queue: this.queue,
      status: this.queueStatus,
      errorQueueIncidents: this.getErrorQueueIncidents(),
      currentError,
    }
  }

  public getErrorQueueIncidents(): QueueIncident[] {
    return this.queue.filter((incident) => incident.status === 'Error')
  }

  private onChange(removedUuid?: string): void {
    const payload = this.getOnChangePayload()

    for (const callback of this.onChangeCallbacks) {
      this.runOnChange(callback, {
        ...payload,
        removedUuid,
      })
    }
  }

  private enqueueIncident(incident: QueueIncident): void {
    enqueue(incident, this.queue)

    if (this.queueStatus === 'idle') {
      this.startProcessing(false)
    } else {
      this.onChange()
    }
  }

  private changeQueueStatus(value: QueueStatus): void {
    this.queueStatus = value
    this.onChange()
  }

  /**
   * Process single incident from queue, by incident `index`
   */
  public async startProcessingQueueIncident(index: number): Promise<void> {
    if (this.queueStatus === 'idle') {
      this.changeQueueStatus('processing')

      const queueIncident = peek(this.queue, index)
      await this.processQueueIncident(queueIncident)
      this.changeQueueStatus('idle')
    } else {
      throw Error('Cannot process when queue is not idle')
    }
  }

  /**
   * Process whole queue.
   */
  public async startProcessing(
    shouldProcessErrorQueueIncident: boolean,
  ): Promise<void> {
    this.changeQueueStatus('processing')

    // Process all incidents that haven't been processed yet
    while (this.queue.length > 0) {
      // Always process the first non-error incident, or first incident if processing errors
      const nextIncidentIndex = this.queue.findIndex(
        (incident) =>
          shouldProcessErrorQueueIncident || incident.status !== 'Error',
      )

      // If no processable incidents found, break
      if (nextIncidentIndex === -1) {
        break
      }

      const queueIncident = peek(this.queue, nextIncidentIndex)
      await this.processQueueIncident(queueIncident)

      // Notify callbacks about queue changes if there are still incidents remaining to process
      if (this.queue.length > 0) {
        this.onChange()
      }
    }

    this.changeQueueStatus('idle')
  }
}
